import { apiClient } from '@/api/client';
import {
  ValidationDetails,
  ValidationCreateRequest,
  ValidationListParams
} from './types';

/**
 * Creates a new validation for a finding
 */
export async function createValidation(
  payload: ValidationCreateRequest,
  options?: {
    enforce_reason?: boolean;
    latest_wins?: boolean;
  }
): Promise<ValidationDetails> {
  const queryParams = new URLSearchParams();

  if (options?.enforce_reason !== undefined) {
    queryParams.append('enforce_reason', options.enforce_reason.toString());
  }
  if (options?.latest_wins !== undefined) {
    queryParams.append('latest_wins', options.latest_wins.toString());
  }

  payload = {
    ...payload,
    author: payload.author ?? 'USER-001'
  };

  const queryString = queryParams.toString();
  const endpoint = `/validations${queryString ? `?${queryString}` : ''}`;

  return apiClient<ValidationDetails>(endpoint, {
    method: 'POST',
    data: payload
  });
}

/**
 * Fetches paginated list of validations with optional filters
 */
export async function fetchValidations(
  params?: ValidationListParams
): Promise<ValidationDetails[]> {
  const queryParams = new URLSearchParams();

  if (params?.skip !== undefined) {
    queryParams.append('skip', params.skip.toString());
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }
  if (params?.finding_id) {
    queryParams.append('finding_id', params.finding_id);
  }
  if (params?.author) {
    queryParams.append('author', params.author);
  }
  if (params?.status) {
    queryParams.append('status', params.status);
  }
  if (params?.reason) {
    queryParams.append('reason', params.reason);
  }

  const queryString = queryParams.toString();
  const endpoint = `/validations${queryString ? `?${queryString}` : ''}`;

  return apiClient<ValidationDetails[]>(endpoint, {
    method: 'GET'
  });
}

/**
 * Fetches the latest validation for a specific finding
 */
export async function fetchLatestValidation(
  findingId: string
): Promise<ValidationDetails> {
  const encodedId = encodeURIComponent(findingId);
  return apiClient<ValidationDetails>(
    `/validations/findings/${encodedId}/latest`,
    {
      method: 'GET'
    }
  );
}

/**
 * Fetches all validations (history) for a specific finding
 */
export async function fetchValidationsForFinding(
  findingId: string,
  params?: {
    skip?: number;
    limit?: number;
  }
): Promise<ValidationDetails[]> {
  const queryParams = new URLSearchParams();

  if (params?.skip !== undefined) {
    queryParams.append('skip', params.skip.toString());
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString());
  }

  const encodedId = encodeURIComponent(findingId);
  const queryString = queryParams.toString();
  const endpoint = `/validations/findings/${encodedId}${queryString ? `?${queryString}` : ''}`;

  return apiClient<ValidationDetails[]>(endpoint, {
    method: 'GET'
  });
}
